import React, { useEffect } from 'react';
import { Zap, PauseCircle, Clock, AlertTriangle } from 'lucide-react';
import { usePortalStore } from '../store';
import type { SpeedMode } from '../types';

const SPEED_LABELS: Record<SpeedMode, { label: string; color: string; icon: React.ReactNode }> = {
  full:            { label: 'Full Speed',     color: 'text-emerald-400', icon: <Zap className="w-4 h-4" /> },
  scheduled:       { label: 'Scheduled',      color: 'text-cyan-400',    icon: <Clock className="w-4 h-4" /> },
  'budget-capped': { label: 'Budget-Capped',  color: 'text-yellow-400',  icon: <AlertTriangle className="w-4 h-4" /> },
  paused:          { label: 'Paused',         color: 'text-red-400',     icon: <PauseCircle className="w-4 h-4" /> },
};

const fmt = (n: number) =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` :
  n >= 1_000 ? `${(n / 1_000).toFixed(0)}K` : String(n);

export const BurnRateGauge: React.FC = () => {
  const { burnRate, loadBurnRate } = usePortalStore();

  useEffect(() => {
    loadBurnRate();
  }, []);

  if (!burnRate) {
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 animate-pulse">
        <div className="h-4 bg-slate-700 rounded w-1/2 mb-3" />
        <div className="h-2 bg-slate-700 rounded w-full" />
      </div>
    );
  }

  const modeInfo = SPEED_LABELS[burnRate.speed_mode] ?? SPEED_LABELS.full;
  const gaugeWidth = Math.min(burnRate.monthly_pct, 100);
  const gaugeColor =
    burnRate.budget_exhausted ? 'bg-red-500' :
    burnRate.monthly_pct >= 90 ? 'bg-orange-500' :
    burnRate.monthly_pct >= 70 ? 'bg-yellow-500' :
    'bg-emerald-500';

  const topDepts = Object.entries(burnRate.department_breakdown)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3);

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg p-5 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider">Token Burn Rate</h2>
        <div className={`flex items-center gap-1.5 text-xs font-semibold ${modeInfo.color}`}>
          {modeInfo.icon}
          {modeInfo.label}
        </div>
      </div>

      {/* Fuel gauge */}
      <div>
        <div className="flex justify-between text-xs text-slate-500 mb-1.5">
          <span>{fmt(burnRate.monthly_used)} used</span>
          <span>{fmt(burnRate.monthly_budget)} budget</span>
        </div>
        <div className="w-full h-3 bg-slate-700 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${gaugeColor}`}
            style={{ width: `${gaugeWidth}%` }}
          />
        </div>
        <div className="flex justify-between text-xs mt-1.5">
          <span className={burnRate.budget_exhausted ? 'text-red-400 font-semibold' : 'text-slate-500'}>
            {burnRate.monthly_pct.toFixed(0)}% of monthly budget
          </span>
          <span className="text-slate-500">{fmt(burnRate.monthly_remaining)} remaining</span>
        </div>
      </div>

      {/* This week stats */}
      <div className="flex gap-4 text-xs text-slate-400">
        <div>
          <span className="block text-slate-500">This week</span>
          <span className="font-semibold text-slate-200">{fmt(burnRate.tokens_this_week)}</span>
        </div>
        <div>
          <span className="block text-slate-500">Projected EOW</span>
          <span className="font-semibold text-slate-200">{fmt(burnRate.projected_week_end)}</span>
        </div>
      </div>

      {/* Top departments */}
      {topDepts.length > 0 && (
        <div className="space-y-1.5">
          <span className="text-xs text-slate-500">Spending by dept (this week)</span>
          {topDepts.map(([dept, tokens]) => (
            <div key={dept} className="flex items-center gap-2 text-xs">
              <span className="text-slate-400 w-28 truncate">{dept}</span>
              <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-cyan-600 rounded-full"
                  style={{
                    width: burnRate.tokens_this_week > 0
                      ? `${(tokens / burnRate.tokens_this_week) * 100}%`
                      : '0%'
                  }}
                />
              </div>
              <span className="text-slate-500 w-10 text-right">{fmt(tokens)}</span>
            </div>
          ))}
        </div>
      )}

      {burnRate.budget_exhausted && (
        <div className="bg-red-950/40 border border-red-700/50 rounded p-3 text-xs text-red-300">
          Budget exhausted — autonomous work is paused. Raise the monthly budget or wait for next month.
        </div>
      )}
    </div>
  );
};
